import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Zap,
  Menu,
  User,
  LayoutDashboard,
  ChevronDown,
  BarChart3,
  GitCompare,
  Code2,
  ArrowRight,
  Link2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
const MobileNavDrawer = React.lazy(() => import('./MobileNavDrawer'));
import { Magnetic } from './Magnetic';
import { DeferredContactModal } from './DeferredContactModal';
import { ThemeToggle } from './ThemeToggle';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useAuth } from '../contexts/AuthContext';
import { BoltyWrapper } from '@/components/ui/BoltyWrapper';

export function Navbar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState<string>('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Elevation on scroll
  useEffect(() => {
    let rafId = 0;
    let lastScrolled = false;
    const checkScroll = () => {
      const nextScrolled = window.scrollY > 20;
      if (nextScrolled !== lastScrolled) {
        lastScrolled = nextScrolled;
        setIsScrolled(nextScrolled);
      }
      rafId = 0;
    };
    const onScroll = () => {
      if (!rafId) rafId = requestAnimationFrame(checkScroll);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    rafId = requestAnimationFrame(checkScroll);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setToolsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Primary top-level navigation items
  const navItems = [
    { id: 'audit', label: t('nav.audit', 'Audit SEO'), href: '/audit', isPage: true, isPill: true },
    { id: 'backlinks', label: t('nav.backlinks', 'Backlinks DR'), href: '/outils/backlinks', isPage: true, isPill: true },
    { id: 'pricing', label: t('nav.pricing', 'Tarifs'), href: '/#pricing', isPage: false, isPill: false },
    { id: 'blog', label: t('nav.blog', 'Blog'), href: '/blog', isPage: true, isPill: false },
  ];

  // Secondary tools (grouped in elegant dropdown)
  const toolItems = [
    {
      label: t('nav.keywords', 'Mots-Clés & Intentions'),
      description: t('nav.keywords_desc', 'Explorez les requêtes Google & IA'),
      href: '/outils/mots-cles',
      icon: BarChart3,
      iconBg: 'bg-blue-500/10 text-blue-500 group-hover:bg-blue-500/20 group-hover:shadow-blue-500/20',
    },
    {
      label: t('nav.competitor_gap', 'Comparateur Concurrence'),
      description: t('nav.competitor_desc', 'Analysez les faiblesses de vos rivaux'),
      href: '/outils/concurrents',
      icon: GitCompare,
      iconBg: 'bg-purple-500/10 text-purple-500 group-hover:bg-purple-500/20 group-hover:shadow-purple-500/20',
    },
    {
      label: t('nav.components', 'Flash UI'),
      description: t('nav.components_desc', 'Composants web & animations React'),
      href: '/composants',
      icon: Code2,
      iconBg: 'bg-emerald-500/10 text-emerald-500 group-hover:bg-emerald-500/20 group-hover:shadow-emerald-500/20',
    },
  ];

  const isToolsActive = ['/outils/mots-cles', '/outils/concurrents', '/composants'].includes(location.pathname);

  // High-precision ScrollSpy for homepage in-page sections
  useEffect(() => {
    if (location.pathname !== '/') {
      setActiveSection('');
      return;
    }

    const sectionIds = ['services', 'portfolio', 'pricing'];

    const handleScroll = () => {
      // When at the very top (Hero)
      if (window.scrollY < 200) {
        setActiveSection('');
        return;
      }

      const threshold = 180;
      let matchedSection = '';

      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= threshold && rect.bottom > threshold) {
            matchedSection = id;
            break;
          }
        }
      }

      if (matchedSection) {
        setActiveSection(matchedSection);
      }
    };

    // Coalesce scroll events: read all geometry once before React updates styles.
    let frame = 0;
    const scheduleScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        handleScroll();
      });
    };
    window.addEventListener('scroll', scheduleScroll, { passive: true });
    const timer = setTimeout(scheduleScroll, 300);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', scheduleScroll);
    };
  }, [location.pathname]);

  const isItemActive = (item: typeof navItems[0]): boolean => {
    if (item.isPage) {
      if (item.id === 'blog') {
        return location.pathname.startsWith('/blog');
      }
      if (item.id === 'services') {
        return location.pathname === '/expertises' || location.pathname === '/services';
      }
      if (item.id === 'portfolio') {
        return location.pathname === '/realisations' || location.pathname === '/portfolio';
      }
      if (item.id === 'backlinks') {
        return location.pathname === '/outils/backlinks' || location.pathname === '/backlinks';
      }
      return location.pathname === item.href;
    }
    if (location.pathname === '/') {
      return activeSection === item.id;
    }
    return false;
  };

  const handleNavClick = (e: React.MouseEvent, item: typeof navItems[0]) => {
    if (!item.isPage) {
      if (location.pathname === '/') {
        e.preventDefault();
        const targetId = item.id;
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          setActiveSection(targetId);
          window.history.replaceState(null, '', `/#${targetId}`);
        }
      }
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 w-full z-50 transition-all duration-300 ${
      isScrolled
        ? 'border-b border-border/80 bg-background/90 backdrop-blur-2xl shadow-sm shadow-black/5 dark:shadow-black/30'
        : 'border-b border-border/40 bg-background/80 backdrop-blur-xl'
    }`}>
      <div className="container mx-auto px-3 sm:px-6 h-20 flex items-center justify-between gap-2 xl:gap-4">
        {/* Logo / Home Link */}
        <Link
          to="/"
          onClick={(e) => {
            if (location.pathname === '/') {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
              setActiveSection('');
              window.history.replaceState(null, '', '/');
            }
          }}
          className="flex items-center gap-1.5 sm:gap-2.5 shrink-0 group cursor-pointer focus:outline-none"
          aria-label="Flash Web Accueil"
        >
          <Magnetic strength={0.3}>
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl overflow-hidden shadow-md shadow-primary/30 group-hover:scale-105 transition-transform shrink-0 border border-primary/35 bg-zinc-950 flex items-center justify-center p-0.5">
              <img 
                src="/logo_agency_thumb_64.webp" 
                alt="Flash Web" 
                width="36"
                height="36"
                decoding="async"
                className="w-full h-full object-cover rounded-lg" 
              />
            </div>
          </Magnetic>
          <span className="font-heading font-extrabold text-lg sm:text-xl tracking-tight whitespace-nowrap text-foreground group-hover:text-primary-ink transition-colors">
            Flash Web
          </span>
        </Link>

        {/* Desktop Navigation Links (Liquid Spring Active Pill) */}
        <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1 p-1 rounded-full bg-background/80 dark:bg-card/60 backdrop-blur-md border border-border/60 dark:border-white/10 shadow-xs" aria-label={t('common.nav_main_label', 'Menu principal')}>
          {navItems.map((item) => {
            const active = isItemActive(item);
            return (
              <Link
                key={item.id}
                to={item.href}
                onClick={(e) => handleNavClick(e, item)}
                className={`relative flex items-center gap-1.5 px-3 py-1.5 xl:px-3.5 xl:py-2 rounded-full text-xs xl:text-sm font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap shrink-0 ${
                  active
                    ? 'text-primary-ink font-bold'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                {/* Liquid Floating Pill Indicator */}
                {active && (
                  <div
                    className="absolute inset-0 rounded-full bg-muted/90 dark:bg-zinc-800/90 border border-border/80 dark:border-white/15 shadow-xs transition-all duration-200"
                  />
                )}
                
                <span className="relative z-10 flex items-center gap-1.5 whitespace-nowrap">
                  {/* Radar Pulse Dot */}
                  {active ? (
                    <span className="relative flex h-2 w-2 shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                    </span>
                  ) : item.isPill ? (
                    <span className="relative flex h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" />
                  ) : null}
                  <span className="whitespace-nowrap">{item.label}</span>
                </span>
              </Link>
            );
          })}

          {/* Outils Dropdown Menu */}
          <div
            ref={dropdownRef}
            className="relative shrink-0"
            onMouseEnter={() => setToolsOpen(true)}
            onMouseLeave={() => setToolsOpen(false)}
          >
            <button
              type="button"
              onClick={() => setToolsOpen(!toolsOpen)}
              className={`relative flex items-center gap-1 text-xs xl:text-sm rounded-full px-3 py-1.5 xl:px-3.5 xl:py-2 transition-colors duration-200 cursor-pointer whitespace-nowrap shrink-0 ${
                isToolsActive
                  ? 'text-foreground font-bold'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 font-medium'
              }`}
              aria-expanded={toolsOpen}
              aria-haspopup="true"
            >
              {isToolsActive && (
                <div
                  className="absolute inset-0 rounded-full bg-muted/90 dark:bg-zinc-800/90 border border-border/80 dark:border-white/15 shadow-xs transition-all duration-200"
                />
              )}

              <span className="relative z-10 flex items-center gap-1.5 whitespace-nowrap">
                {isToolsActive && (
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                  </span>
                )}
                <span className="whitespace-nowrap">{t('nav.tools', 'Outils')}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${toolsOpen ? 'rotate-180 text-primary-ink' : ''}`} />
              </span>
            </button>

            {/* Dropdown Floating Card */}
            {toolsOpen && (
              <div
                className="absolute top-full right-0 left-auto translate-x-0 pt-2.5 w-84 max-w-[calc(100vw-2rem)] z-50 animate-in fade-in zoom-in-95 duration-150"
              >
                <div className="relative rounded-2xl border border-border/70 bg-background/95 dark:bg-card/95 backdrop-blur-2xl p-2 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.18)] dark:shadow-[0_25px_70px_-15px_rgba(0,0,0,0.85)] ring-1 ring-white/10 overflow-hidden">
                  {/* Top specular glow line */}
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

                  <div className="flex flex-col gap-1">
                    {toolItems.map((tool) => {
                      const active = location.pathname === tool.href;
                      const Icon = tool.icon;
                      return (
                        <Link
                          key={tool.href}
                          to={tool.href}
                          onClick={() => setToolsOpen(false)}
                          className={`group flex items-center justify-between p-2.5 rounded-xl transition-all duration-200 cursor-pointer ${
                            active
                              ? 'bg-muted/80 text-foreground border border-border/80 shadow-xs'
                              : 'hover:bg-muted/50 text-foreground'
                          }`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-105 shadow-xs ${tool.iconBg}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold flex items-center gap-1.5">
                                <span className="text-foreground group-hover:text-primary-ink transition-colors">
                                  {tool.label}
                                </span>
                                {active && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground truncate">{tool.description}</p>
                            </div>
                          </div>
                          <ArrowRight className="w-3.5 h-3.5 text-primary-ink opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all shrink-0 ms-2" />
                        </Link>
                      );
                    })}
                  </div>

                  {/* Dropdown Agency Footer */}
                  <div className="mt-1.5 pt-2 border-t border-border/50 px-2.5 py-1 flex items-center justify-between text-[11px] text-muted-foreground">
                    <span className="flex items-center gap-1.5 font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      Suite autonome 100% offerte
                    </span>
                    <span className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground">Flash Studio</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Desktop Controls & CTA */}
        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <LanguageSwitcher />
          <ThemeToggle />

          {user ? (
            <Link
              to={user.role === 'admin' ? '/fw-control-center' : '/dashboard'}
              className={`text-sm font-semibold transition-colors flex items-center gap-1.5 px-3 py-1.5 rounded-lg whitespace-nowrap ${
                location.pathname === '/dashboard' || location.pathname === '/fw-control-center'
                  ? 'text-foreground font-bold bg-muted border border-border shadow-2xs'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <LayoutDashboard className="w-4 h-4 text-primary" />
              <span>{user.role === 'admin' ? t('nav.admin', 'Admin') : t('nav.dashboard', 'Espace Client')}</span>
            </Link>
          ) : (
            <Link
              to="/login"
              rel="nofollow"
              className={`text-sm font-semibold transition-colors flex items-center gap-1.5 px-3 py-1.5 rounded-lg whitespace-nowrap ${
                location.pathname === '/login'
                  ? 'text-foreground font-bold bg-muted border border-border shadow-2xs'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <User className="w-4 h-4" />
              <span>{t('nav.client_space', 'Espace Client')}</span>
            </Link>
          )}

          <Magnetic strength={0.2}>
            <BoltyWrapper placement="bottom" size="sm">
              <DeferredContactModal
                defaultPack="Croissance"
                defaultMessage={t('contact_extra.default_project_msg', "Bonjour, j'aimerais échanger sur un projet avec Flash Web.")}
              >
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-full px-5 py-2.5 font-bold text-sm bg-primary hover:bg-primary/90 text-primary-foreground shadow-md shadow-primary/25 hover:shadow-lg hover:shadow-primary/35 hover:scale-[1.02] active:scale-[0.98] cursor-pointer transition-all whitespace-nowrap"
                >
                  {t('nav.direct_contact', 'Contact direct')}
                </Link>
              </DeferredContactModal>
            </BoltyWrapper>
          </Magnetic>
        </div>

        {/* Mobile Menu Button & Controls */}
        <div className="lg:hidden flex items-center gap-1 sm:gap-2 shrink-0">
          <LanguageSwitcher />
          <ThemeToggle />
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl hover:bg-muted cursor-pointer shrink-0 p-1 md:hidden"
            aria-label={t('common.open_menu', 'Ouvrir le menu de navigation')}
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
          </Button>

          {mobileOpen && (
            <React.Suspense fallback={null}>
              <MobileNavDrawer
                open={mobileOpen}
                onOpenChange={setMobileOpen}
                navItems={navItems}
                toolItems={toolItems}
                isItemActive={isItemActive}
                handleNavClick={handleNavClick}
                user={user}
              />
            </React.Suspense>
          )}
        </div>
      </div>
    </header>
  );
}
